import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Phone, Mail, MessageCircle, Users as UsersIcon, MapPin, Bed, Wine, Linkedin } from 'lucide-react';
import TeamChatDialog from './TeamChatDialog';
import DiningConceptDialog from './DiningConceptDialog';
import RestaurantDetailDialog from './RestaurantDetailDialog';
import RestrictedEmail from './RestrictedEmail';
import RestrictedPhone from './RestrictedPhone';
import { formatPhone } from '../lib/phone';

// iter-80 — Slugs whose email is masked from non-admin viewers. These
// are ownership/executive contacts where the regular user shouldn't
// see the address until an admin approves disclosure. Maintained as a
// hard-coded set so it's auditable in code review; can move to the DB
// later if the list churns.
const RESTRICTED_EMAIL_SLUGS = new Set([
  'panna-utpaul',
  'pedro-pereira',
  'dwight-tabales',
]);

// iter-81 — Slugs whose phone number is masked from non-admin viewers.
// Pairs with the backend scrub in property_routes.get_property_public()
// — the backend strips `phone`/`phone_fmt` and stamps `phone_restricted:
// true` on these members for non-admin callers. The frontend uses this
// set as a fallback in case the backend flag is absent (e.g. stale
// cache) so we still render the masked UI.
const RESTRICTED_PHONE_SLUGS = new Set([
  'pedro-pereira',
  'dwight-tabales',
]);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function whatsappLink(phone) {
  // wa.me/ expects digits only (no +, no spaces, no dashes)
  return `https://wa.me/${(phone || '').replace(/[^0-9]/g, '')}`;
}

// Normalize a LinkedIn URL so `linkedin.com/in/foo` and `www.linkedin.com/...`
// still open as absolute external URLs. Without this, the browser treats a
// scheme-less value as relative to the current origin and the click does
// nothing useful.
function linkedinHref(raw) {
  const v = (raw || '').trim();
  if (!v) return '';
  if (/^https?:\/\//i.test(v)) return v;
  return `https://${v.replace(/^\/+/, '')}`;
}

// Map team-member `company` slug → branded logo tile metadata.
// `dark` flag means the logo PNG already has a dark background baked in
// so we keep the badge dark; otherwise we render on white.
const COMPANY_LOGOS = {
  hyatt:        { src: '/logo-hyatt-inclusive.jpg', alt: 'World of Hyatt — Inclusive Collection', dark: false },
  tldi:         { src: '/logo-tldi.png',            alt: 'TLDI · Tourism & Leisure Development International', dark: true  },
  tortuga:      { src: '/logo-grupo-tortuga.jpg',   alt: 'Grupo Tortuga',                          dark: true  },
  'heavenly-ai':{ src: '/logo-heavenly-ai.png',     alt: 'Heavenly AI Solutions',                  dark: false },
};

function CompanyBadge({ company }) {
  // iter-76: DB stores companies as `"Hyatt"`, `"TLDI"`, `"Tortuga"`
  // (capitalised), but the slug keys above are lowercase. Normalise
  // before lookup so the team grid actually shows the partner logo
  // next to each member's name.
  const key = (company || '').toString().trim().toLowerCase();
  const meta = COMPANY_LOGOS[key];
  if (!meta) return null;
  return (
    <span
      data-testid={`company-badge-${key}`}
      className="inline-flex items-center justify-center rounded-md flex-shrink-0 overflow-hidden"
      style={{
        width: 36,
        height: 36,
        backgroundColor: meta.dark ? '#0E1F2C' : 'white',
        border: '1px solid rgba(0,0,0,0.06)',
        padding: 3,
      }}
      title={meta.alt}
    >
      <img src={meta.src} alt={meta.alt} className="block w-full h-full" style={{ objectFit: 'contain' }} />
    </span>
  );
}

// Partner logo card — Apple-style chip used above the team grid to show
// Operator / Ownership / General Contractor in one row. `kind` drives the
// small uppercase kicker; `company` indexes into COMPANY_LOGOS.
function PartnerLogoCard({ kind, company, name, tag }) {
  const key = (company || '').toString().trim().toLowerCase();
  const meta = COMPANY_LOGOS[key];
  if (!meta) return null;
  const KICKERS = { operator: 'Operator', ownership: 'Ownership', gc: 'General Contractor' };
  return (
    <div
      data-testid={`partner-logo-${kind}`}
      className="flex items-center gap-3 px-4 py-3 rounded-2xl transition-all hover:-translate-y-0.5"
      style={{
        backgroundColor: 'white',
        border: '1px solid #EAE5D9',
        boxShadow: '0 4px 18px rgba(0,61,165,0.06)',
        minWidth: 220,
      }}
      title={meta.alt}
    >
      <span
        className="inline-flex items-center justify-center rounded-lg flex-shrink-0 overflow-hidden"
        style={{
          width: 52,
          height: 52,
          backgroundColor: meta.dark ? '#0E1F2C' : 'white',
          border: '1px solid rgba(0,0,0,0.06)',
          padding: 6,
        }}
      >
        <img src={meta.src} alt={meta.alt} className="block w-full h-full" style={{ objectFit: 'contain' }} />
      </span>
      <div className="min-w-0">
        <p className="text-[9px] uppercase tracking-[0.18em] font-bold" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>
          {KICKERS[kind] || kind}
        </p>
        <p className="text-sm font-bold leading-tight" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{name}</p>
        <p className="text-[10px]" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>{tag}</p>
      </div>
    </div>
  );
}

function FactRow({ icon: Icon, label, value, children }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b last:border-b-0" style={{ borderColor: '#EAE5D9' }}>
      <div className="mt-0.5 w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#1A2E2E' }}>
        <Icon className="w-3.5 h-3.5" style={{ color: '#A3D4D4' }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-[0.15em] font-bold mb-0.5" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>{label}</p>
        {value && <p className="text-sm font-medium" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{value}</p>}
        {children}
      </div>
    </div>
  );
}

function TeamCard({ member, onChat }) {
  const initials = (member.name || '?').split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div
      data-testid={`team-card-${member.slug}`}
      className="p-4 rounded-2xl flex flex-col gap-2 transition-all hover:-translate-y-0.5"
      style={{ backgroundColor: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.04)', borderTop: `3px solid ${member.accent}` }}
    >
      {/* Header row: photo/avatar + name/role + company badge */}
      <div className="flex items-start gap-3">
        <div
          className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center border-2"
          style={{ borderColor: member.accent, backgroundColor: `${member.accent}15` }}
          data-testid={`team-avatar-${member.slug}`}
        >
          {member.photo_url ? (
            <img src={member.photo_url} alt={member.name} className="w-full h-full object-cover" />
          ) : (
            <span className="text-base font-bold" style={{ color: member.accent, fontFamily: 'Manrope' }}>{initials}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-base font-bold leading-tight" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{member.name}</p>
          <p className="text-[11px] mt-0.5 font-medium" style={{ color: member.accent, fontFamily: 'Manrope' }}>{member.role}</p>
        </div>
        {member.company && <CompanyBadge company={member.company} />}
      </div>

      {/* Bio — renders only when present */}
      {member.bio && (
        <p
          className="text-[11px] italic leading-snug mt-1"
          style={{ color: '#4A5D5D', fontFamily: "'Cormorant Garamond', serif" }}
          data-testid={`team-bio-${member.slug}`}
        >
          {member.bio}
        </p>
      )}

      <div className="flex flex-col gap-1.5 mt-1">
        {/* iter-81 — Phone row. Four modes:
            0. iter-92: User opted to hide their phone (phone_hidden_by_user)
               → render nothing (no unmask UI). Admins still see the real
               value because the backend doesn't scrub for privileged callers.
            1. Restricted (Pedro/Dwight, non-admin caller) → backend
               scrubbed the phone and stamped `phone_restricted: true`
               → render <RestrictedPhone> with blur + Request button.
            2. Has phone → render the regular tel:/WhatsApp/LinkedIn row.
            3. No phone → render nothing (LinkedIn-only row below
               handles the linkedin-without-phone case). */}
        {member.phone_hidden_by_user ? null : (member.phone_restricted || (!member.phone && RESTRICTED_PHONE_SLUGS.has(member.slug))) ? (
          <RestrictedPhone memberSlug={member.slug} memberName={member.name} />
        ) : member.phone && (
          <div className="flex items-center gap-2 text-[12px]" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
            <a href={`tel:${member.phone}`} className="flex-1 flex items-center gap-1.5 hover:underline" data-testid={`call-${member.slug}`}>
              <Phone className="w-3.5 h-3.5" style={{ color: '#1F4FBE' }} />
              <span className="tabular-nums">{formatPhone(member.phone) || member.phone_fmt || member.phone}</span>
            </a>
            <a
              href={whatsappLink(member.phone)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`whatsapp-${member.slug}`}
              title="Send WhatsApp message"
              className="w-7 h-7 rounded-full flex items-center justify-center transition-transform hover:scale-110"
              style={{ backgroundColor: '#25D366' }}
            >
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="white" aria-hidden="true">
                <path d="M17.5 14.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.2-.2.3-.8.9-1 1.1-.2.2-.4.2-.7.1-1.2-.6-2-1.1-2.7-2.4-.2-.4 0-.4.3-.7.1-.1.2-.3.4-.5l.2-.4c.1-.2 0-.4-.1-.5l-.7-1.7c-.2-.4-.4-.4-.6-.4-.2 0-.4 0-.6 0-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4 0 1.4 1 2.8 1.2 3 .1.2 2 3 4.7 4.2 1.6.7 2.3.7 3.1.6.5-.1 1.6-.7 1.9-1.3.2-.7.2-1.2.2-1.3-.1-.1-.3-.2-.6-.3zM12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.4 1.3 4.9L2 22l5.3-1.4C8.7 21.5 10.3 22 12 22c5.5 0 10-4.5 10-10S17.5 2 12 2z"/>
              </svg>
            </a>
            {member.linkedin_url && (
              <a
                href={linkedinHref(member.linkedin_url)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`linkedin-${member.slug}`}
                title="View LinkedIn profile"
                className="w-7 h-7 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                style={{ backgroundColor: '#0A66C2' }}
              >
                <Linkedin className="w-3.5 h-3.5" style={{ color: 'white' }} />
              </a>
            )}
          </div>
        )}
        {/* LinkedIn row when no phone — so it still renders for members with only LinkedIn */}
        {!member.phone && member.linkedin_url && (
          <a
            href={linkedinHref(member.linkedin_url)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 text-[11px] hover:underline"
            style={{ color: '#0A66C2', fontFamily: 'Manrope' }}
            data-testid={`linkedin-${member.slug}`}
          >
            <Linkedin className="w-3.5 h-3.5" /> LinkedIn profile
          </a>
        )}

        {/* Email row — restricted for ownership/executive contacts so
            non-admins can't scrape the addresses. Admins always see the
            plain mailto. iter-92: When the user has self-hidden their
            email (email_hidden_by_user), render nothing. */}
        {member.email_hidden_by_user ? null : RESTRICTED_EMAIL_SLUGS.has(member.slug) ? (
          <RestrictedEmail email={member.email} memberSlug={member.slug} memberName={member.name} />
        ) : (
          <a
            href={`mailto:${member.email}`}
            className="flex items-center gap-1.5 text-[11px] truncate hover:underline"
            style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}
            data-testid={`email-${member.slug}`}
          >
            <Mail className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#1F4FBE' }} />
            <span className="truncate">{member.email}</span>
          </a>
        )}
      </div>

      {/* In-app chat CTA */}
      <button
        onClick={() => onChat(member)}
        data-testid={`chat-${member.slug}`}
        className="mt-2 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all"
        style={{ backgroundColor: '#1A2E2E', color: '#A3D4D4', fontFamily: 'Manrope' }}
      >
        <MessageCircle className="w-3.5 h-3.5" /> Open in-app chat
      </button>
    </div>
  );
}

export default function PropertyDetailsSection() {
  const [chatTarget, setChatTarget] = useState(null);
  const [data, setData] = useState(null);
  const [openOutlet, setOpenOutlet] = useState(null);

  useEffect(() => {
    axios.get(`${API}/property`).then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;
  const { team = [], fnb = [], title, subtitle, operator_name, operator_tagline, operator_logo_url,
          ownership_name, ownership_tagline, ownership_logo_url, rooms, plot_size,
          brand_name, brand_tagline, brand_logo_url,
          reconstruction_partner_name, reconstruction_partner_tagline, reconstruction_partner_logo_url } = data;

  return (
    <section
      id="property-details"
      data-testid="property-details-section"
      className="py-16 px-4 sm:px-6 lg:px-10"
      style={{ backgroundColor: '#FAF8F3' }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: '#1A2E2E', color: '#A3D4D4' }}>
            <MapPin className="w-3.5 h-3.5" />
            <span className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ fontFamily: 'Manrope' }}>
              Property Details
            </span>
          </div>
          <h2
            className="text-4xl sm:text-5xl lg:text-6xl mb-2"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E', fontWeight: 500 }}
          >
            {title}
          </h2>
          <p className="text-base italic" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#7A8C8C' }}>
            {subtitle}
          </p>
        </div>

        {/* Top facts row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
          {/* Operator + Ownership card */}
          <div className="p-6 rounded-2xl" style={{ backgroundColor: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold mb-3" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
              Brand · Operator · Ownership
            </p>

            {/* Brand tile — the master / collection brand under which the
                property operates (e.g. Hyatt Inclusive Collection).
                Renders only when configured so older properties without a
                brand_* set don't show an empty row. */}
            {(brand_logo_url || brand_name) && (
              <div className="flex items-center gap-3 mb-4 pb-4 border-b" style={{ borderColor: '#EAE5D9' }} data-testid="property-brand-tile">
                {brand_logo_url && (
                  <img src={brand_logo_url} alt={brand_name} className="h-12 w-auto rounded" style={{ objectFit: 'contain' }} />
                )}
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>Brand</p>
                  <p className="text-sm font-bold" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{brand_name}</p>
                  {brand_tagline && (
                    <p className="text-[11px]" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>{brand_tagline}</p>
                  )}
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 mb-4 pb-4 border-b" style={{ borderColor: '#EAE5D9' }} data-testid="property-operator-tile">
              <img src={operator_logo_url} alt={operator_name} className="h-12 w-auto rounded" style={{ objectFit: 'contain' }} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>Operator</p>
                <p className="text-sm font-bold" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{operator_name}</p>
                <p className="text-[11px]" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>{operator_tagline}</p>
              </div>
            </div>

            <div className="flex items-center gap-3" data-testid="property-ownership-tile">
              <img src={ownership_logo_url} alt={ownership_name} className="h-12 w-12 rounded-lg" style={{ objectFit: 'cover', backgroundColor: '#0E1F2C' }} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>Ownership</p>
                <p className="text-sm font-bold" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{ownership_name}</p>
                <p className="text-[11px]" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>{ownership_tagline}</p>
              </div>
            </div>

            {/* Reconstruction Partner / GC row — only renders when a partner
                logo+name are configured in the Property CMS. */}
            {reconstruction_partner_logo_url && reconstruction_partner_name && (
              <div className="flex items-center gap-3 mt-4 pt-4 border-t" style={{ borderColor: '#EAE5D9' }} data-testid="reconstruction-partner">
                <img
                  src={reconstruction_partner_logo_url}
                  alt={reconstruction_partner_name}
                  className="h-12 w-12 rounded-lg"
                  style={{ objectFit: 'contain', backgroundColor: '#0E1F2C', padding: 4 }}
                />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>Reconstruction Partner</p>
                  <p className="text-sm font-bold" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{reconstruction_partner_name}</p>
                  <p className="text-[11px]" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>{reconstruction_partner_tagline}</p>
                </div>
              </div>
            )}
          </div>

          {/* Quick facts card */}
          <div className="p-6 rounded-2xl" style={{ backgroundColor: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold mb-2" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
              Quick Facts
            </p>
            <FactRow icon={Bed} label="Rooms" value={`${rooms} keys`} />
            <FactRow icon={MapPin} label="Plot Size" value={plot_size} />
            <FactRow icon={Wine} label="F&B Outlets" value={`${fnb.length} concepts`} />
            <FactRow icon={UsersIcon} label="Project Team" value={`${team.length} members on this transformation`} />
          </div>
        </div>

        {/* F&B Outlets */}
        <div className="p-6 rounded-2xl mb-10" style={{ backgroundColor: 'white', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>Food &amp; Beverage</p>
              <h3 className="text-2xl mt-0.5" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E' }}>{fnb.length} dining concepts</h3>
            </div>
            <span className="px-3 py-1 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#EAE5D9', color: '#1A2E2E', fontFamily: 'Manrope' }}>
              <Wine className="w-3 h-3 inline mr-1 -mt-0.5" /> Inclusive
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {fnb.map((o, i) => {
              const accent = o.accent_color || '#003DA5';
              const logoSrc = o.logo_url
                ? (o.logo_url.startsWith('http') ? o.logo_url : `${process.env.REACT_APP_BACKEND_URL}${o.logo_url}`)
                : '';
              return (
                <button
                  type="button"
                  key={o.id || o.name}
                  onClick={() => setOpenOutlet(o)}
                  className="px-3 py-2.5 rounded-xl flex items-center gap-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
                  style={{ backgroundColor: '#FAF8F3', border: `1px solid ${accent}30` }}
                  data-testid={`fnb-${i}`}
                  title={`Open ${o.name}`}
                >
                  {/* Restaurant logo — falls back to the numbered chip when
                      the outlet hasn't uploaded one yet. */}
                  <span
                    className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{
                      backgroundColor: logoSrc ? 'white' : '#1A2E2E',
                      color: '#A3D4D4',
                      fontFamily: 'Manrope',
                      border: logoSrc ? `1px solid ${accent}30` : 'none',
                    }}
                  >
                    {logoSrc ? (
                      <img
                        src={logoSrc}
                        alt={`${o.name} logo`}
                        className="max-h-full max-w-full object-contain p-1"
                        loading="lazy"
                      />
                    ) : (
                      <span className="text-xs font-bold">{i + 1}</span>
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] font-bold leading-tight" style={{ color: '#1A2E2E', fontFamily: 'Manrope' }}>{o.name}</p>
                    <p className="text-[10px] mt-0.5 truncate" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>{o.detail}</p>
                  </div>
                  {((o.photos || []).length + (o.renders || []).length + (o.menu_samples || []).length > 0) && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: accent, color: 'white', fontFamily: 'Manrope' }}>
                      {(o.photos || []).length + (o.renders || []).length + (o.menu_samples || []).length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Meet the team */}
        <div>
          <div className="text-center mb-6">
            <p className="text-[10px] uppercase tracking-[0.15em] font-bold mb-1" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>
              <UsersIcon className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Meet the Team
            </p>
            <h3 className="text-3xl sm:text-4xl" style={{ fontFamily: "'Cormorant Garamond', serif", color: '#1A2E2E', fontWeight: 500 }}>
              The people behind the rebuild
            </h3>
            <p className="mt-2 text-sm" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>
              Tap the WhatsApp icon to message directly, or open the in-app chat.
            </p>
          </div>

          {/* Partner logos strip — Operator · Ownership · General Contractor.
              Sits above the team grid so visitors immediately see the three
              organisations driving the reconstruction. */}
          <div
            data-testid="partner-logos-strip"
            className="flex flex-wrap items-stretch justify-center gap-3 sm:gap-5 mb-8"
          >
            <PartnerLogoCard kind="operator" company="hyatt" name="Hyatt Inclusive Collection" tag="Operator" />
            <PartnerLogoCard kind="ownership" company="tortuga" name="Grupo Tortuga" tag="Ownership" />
            <PartnerLogoCard kind="gc" company="tldi" name="TLDI" tag="General Contractor" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {team.map((m) => (
              <TeamCard key={m.slug} member={m} onChat={setChatTarget} />
            ))}
          </div>

          {/* iter-88 — Hover-revealed "extras" strip. Lists every app
              user who isn't already on the headline operator team. The
              container is collapsed to 0 height by default and grows
              smoothly on hover to expose the additional faces. Touch
              devices also expand on tap (via :focus-within fallback). */}
          {(data?.extra_team?.length ?? 0) > 0 && (
            <ExtrasReveal extras={data.extra_team} onChat={setChatTarget} />
          )}
        </div>
      </div>

      <TeamChatDialog
        open={Boolean(chatTarget)}
        onClose={() => setChatTarget(null)}
        recipient={chatTarget?.slug}
        recipientName={chatTarget?.name}
      />
      <RestaurantDetailDialog
        outlet={openOutlet}
        open={Boolean(openOutlet)}
        onClose={() => setOpenOutlet(null)}
        canEdit={Boolean(typeof window !== 'undefined' && localStorage.getItem('admin_token'))}
        onUpdated={(next) => {
          if (!next) return;
          setData((d) => d ? { ...d, fnb: (d.fnb || []).map((o) => (o.id === next.id ? next : o)) } : d);
          setOpenOutlet(next);
        }}
      />
    </section>
  );
}


/* iter-88 — ExtrasReveal.

A hover-expand band placed beneath the operator team grid. Closed
state: a slim "Hover to see N more contributors →" rail. Open state:
the rail grows to a 3-column grid of compact team cards (or initials
chips when there's no photo). Closes ~400ms after the cursor leaves.

Why an explicit grow instead of a normal grid? The user wanted the
default landing-page view to stay focused on the operator team; the
"app users" who aren't formally on the team list should be a
discoverable add-on, not the headline. Hover-to-extend matches that
intent exactly. */
function ExtrasReveal({ extras = [], onChat }) {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef(null);

  const handleEnter = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpen(true);
  };
  const handleLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 400);
  };

  return (
    <div
      data-testid="team-extras-reveal"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleEnter}
      onBlur={handleLeave}
      className="mt-6"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}        // also tappable on touch
        data-testid="team-extras-toggle"
        aria-expanded={open}
        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border-2 border-dashed transition-all"
        style={{
          borderColor: open ? '#1F4FBE' : '#D9E3F1',
          background: open ? '#EFF6FF' : 'transparent',
          color: open ? '#003DA5' : '#4A5D5D',
          fontFamily: 'Manrope',
          fontWeight: 700,
          fontSize: 12,
        }}
      >
        <UsersIcon className="w-3.5 h-3.5" />
        {open
          ? `Hide ${extras.length} more contributors`
          : `Hover to see ${extras.length} more contributor${extras.length === 1 ? '' : 's'} →`}
      </button>

      <div
        data-testid="team-extras-grid"
        style={{
          maxHeight: open ? 2400 : 0,
          opacity: open ? 1 : 0,
          overflow: 'hidden',
          transition: 'max-height 600ms ease, opacity 400ms ease, margin 400ms ease',
          marginTop: open ? 16 : 0,
        }}
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {extras.map((m) => (
            <ExtraTeamCard key={m.slug + m.email} member={m} onChat={onChat} />
          ))}
        </div>
      </div>
    </div>
  );
}


/* Compact card for the hover-revealed extras grid. Photo when present,
   coloured initials chip when not. Click opens a chat with the team
   slug (same `onChat` pipe as the headline TeamCard). */
function ExtraTeamCard({ member, onChat }) {
  const initials = (member.name || '?')
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || '').join('') || '?';

  return (
    <button
      type="button"
      onClick={() => onChat?.(member)}
      data-testid={`team-extra-card-${member.slug}`}
      className="group flex items-center gap-3 p-3 rounded-lg border transition-all hover:shadow-md text-left"
      style={{ borderColor: '#EAE5D9', background: 'white', fontFamily: 'Manrope' }}
    >
      {member.photo_url ? (
        <img
          src={member.photo_url}
          alt={member.name}
          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
          style={{ border: '2px solid white', boxShadow: '0 1px 4px rgba(0,0,0,.12)' }}
        />
      ) : (
        <span
          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
          style={{ background: 'linear-gradient(135deg,#1F4FBE,#003DA5)' }}
          aria-hidden="true"
        >
          {initials}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-bold truncate" style={{ color: '#1A2E2E' }}>{member.name}</p>
        <p className="text-[10px] truncate" style={{ color: '#7A8C8C' }}>
          {member.title || (member.departments?.[0] ? `${member.departments[0]} team` : 'Contributor')}
        </p>
      </div>
    </button>
  );
}

